import { useCallback, useRef } from 'react';

/**
 * Play a distinctive three-tone ascending chime using the Web Audio API.
 * No external audio file is required.
 */
export function useNotificationSound() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastPlayedRef = useRef<number>(0);

  const play = useCallback(async () => {
    try {
      // Throttle: skip if played within the last 1 second
      const timestamp = Date.now();
      if (timestamp - lastPlayedRef.current < 1000) return;
      lastPlayedRef.current = timestamp;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      const ctx = audioContextRef.current;

      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      const now = ctx.currentTime;

      // Three-tone ascending chime (C6 → E6 → G6) with harmonics
      const tones = [
        { freq: 1047, start: 0, duration: 0.15 },      // C6
        { freq: 1319, start: 0.12, duration: 0.15 },    // E6
        { freq: 1568, start: 0.24, duration: 0.25 },    // G6 (longer for resonance)
      ];

      for (const tone of tones) {
        // Main tone (triangle wave for richer timbre)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = tone.freq;
        osc.type = 'triangle';
        gain.gain.setValueAtTime(0.35, now + tone.start);
        gain.gain.exponentialRampToValueAtTime(0.01, now + tone.start + tone.duration);
        osc.start(now + tone.start);
        osc.stop(now + tone.start + tone.duration);

        // Harmonic overtone (sine, one octave up, quieter)
        const oscH = ctx.createOscillator();
        const gainH = ctx.createGain();
        oscH.connect(gainH);
        gainH.connect(ctx.destination);
        oscH.frequency.value = tone.freq * 2;
        oscH.type = 'sine';
        gainH.gain.setValueAtTime(0.1, now + tone.start);
        gainH.gain.exponentialRampToValueAtTime(0.01, now + tone.start + tone.duration * 0.8);
        oscH.start(now + tone.start);
        oscH.stop(now + tone.start + tone.duration);
      }
    } catch {
      // Silently ignore audio errors (e.g. unsupported browser)
    }
  }, []);

  return play;
}
